// Character-avatar catalog + SVG part renderers.
//
// A member's avatar is composed from four independent slots — a backdrop, a
// character, an outfit, and a hat — layered inside a single 100×100 SVG (see
// CharacterAvatar.tsx). This module is the client mirror of the server's
// closed option vocabularies in api/schemas/avatar.py; the ids here MUST match
// those Literal values exactly.
//
// Every part is drawn in the same 100×100 coordinate space:
//   • backdrop fills the whole square (the avatar frame clips it to a circle)
//   • the character head is centred on (50, 52), radius ≈ 24 (spans y 28–76)
//   • outfits sit at the bottom (y ≈ 74–100), peeking out below the head
//   • hats sit on top of the head (y ≈ 4–40)
// Parts use flat fills + simple overlays (no <defs>/gradients) so many avatars
// can render on one page without SVG id collisions.
import type { ReactElement } from "react";

// ---------------------------------------------------------------------------
// Types — mirror api/schemas/avatar.py
// ---------------------------------------------------------------------------

export type AvatarCharacter =
  | "astronaut"
  | "alien"
  | "robot"
  | "cow"
  | "rocket"
  | "star"
  | "moon"
  | "planet";

export type AvatarHat =
  | "none"
  | "helmet"
  | "party"
  | "propeller"
  | "halo"
  | "crown"
  | "antenna";

export type AvatarOutfit =
  | "none"
  | "spacesuit"
  | "labcoat"
  | "cape"
  | "overalls"
  | "bowtie"
  | "scarf";

export type AvatarBackdrop =
  | "cosmos"
  | "nebula"
  | "deepspace"
  | "meadow"
  | "void"
  | "sunrise";

export interface AvatarConfig {
  character: AvatarCharacter;
  hat: AvatarHat;
  outfit: AvatarOutfit;
  backdrop: AvatarBackdrop;
}

export const DEFAULT_AVATAR: AvatarConfig = {
  character: "astronaut",
  hat: "none",
  outfit: "none",
  backdrop: "cosmos",
};

export interface Option<T extends string> {
  id: T;
  label: string;
  emoji: string;
}

export const CHARACTERS: Option<AvatarCharacter>[] = [
  { id: "astronaut", label: "Astronaut", emoji: "🧑‍🚀" },
  { id: "alien", label: "Alien", emoji: "👽" },
  { id: "robot", label: "Robot", emoji: "🤖" },
  { id: "cow", label: "Cow", emoji: "🐄" },
  { id: "rocket", label: "Rocket", emoji: "🚀" },
  { id: "star", label: "Star", emoji: "⭐" },
  { id: "moon", label: "Moon", emoji: "🌙" },
  { id: "planet", label: "Planet", emoji: "🪐" },
];

export const HATS: Option<AvatarHat>[] = [
  { id: "none", label: "No hat", emoji: "🚫" },
  { id: "helmet", label: "Space helmet", emoji: "🪖" },
  { id: "party", label: "Party hat", emoji: "🎉" },
  { id: "propeller", label: "Propeller cap", emoji: "🁢" },
  { id: "halo", label: "Halo", emoji: "😇" },
  { id: "crown", label: "Crown", emoji: "👑" },
  { id: "antenna", label: "Antennae", emoji: "📡" },
];

export const OUTFITS: Option<AvatarOutfit>[] = [
  { id: "none", label: "No outfit", emoji: "🚫" },
  { id: "spacesuit", label: "Spacesuit", emoji: "🧑‍🚀" },
  { id: "labcoat", label: "Lab coat", emoji: "🥼" },
  { id: "cape", label: "Cape", emoji: "🦸" },
  { id: "overalls", label: "Overalls", emoji: "👖" },
  { id: "bowtie", label: "Bow tie", emoji: "🎀" },
  { id: "scarf", label: "Scarf", emoji: "🧣" },
];

export const BACKDROPS: {
  id: AvatarBackdrop;
  label: string;
  base: string;
  glow: string;
  stars: boolean;
}[] = [
  { id: "cosmos", label: "Cosmos", base: "#2f2260", glow: "#6a4fb0", stars: true },
  { id: "nebula", label: "Nebula", base: "#5c1f4c", glow: "#c14b95", stars: true },
  { id: "deepspace", label: "Deep space", base: "#10305e", glow: "#3f7fd0", stars: true },
  { id: "meadow", label: "Meadow", base: "#4c9d5e", glow: "#a7de8f", stars: false },
  { id: "void", label: "Void", base: "#191a20", glow: "#4a4c58", stars: true },
  { id: "sunrise", label: "Sunrise", base: "#e07a45", glow: "#ffd08a", stars: false },
];

// ---------------------------------------------------------------------------
// Shared face atoms (reused across several characters)
// ---------------------------------------------------------------------------

function Eyes({ cx1 = 42, cx2 = 58, cy = 50, r = 3, fill = "#1c2230" }): ReactElement {
  return (
    <>
      <circle cx={cx1} cy={cy} r={r} fill={fill} />
      <circle cx={cx2} cy={cy} r={r} fill={fill} />
      <circle cx={cx1 + 1} cy={cy - 1} r={r * 0.35} fill="#fff" />
      <circle cx={cx2 + 1} cy={cy - 1} r={r * 0.35} fill="#fff" />
    </>
  );
}

function Smile({ y = 60, w = 8, stroke = "#1c2230" }): ReactElement {
  return (
    <path
      d={`M${50 - w} ${y} Q50 ${y + 6} ${50 + w} ${y}`}
      fill="none"
      stroke={stroke}
      strokeWidth={2}
      strokeLinecap="round"
    />
  );
}

// ---------------------------------------------------------------------------
// Backdrop
// ---------------------------------------------------------------------------

const STAR_POINTS = [
  [18, 20],
  [80, 26],
  [30, 76],
  [72, 70],
  [86, 52],
  [14, 54],
];

export function renderBackdrop(id: AvatarBackdrop): ReactElement {
  const b = BACKDROPS.find((x) => x.id === id) ?? BACKDROPS[0];
  return (
    <g>
      <rect x="0" y="0" width="100" height="100" fill={b.base} />
      {/* Soft glow toward the top so the head reads against it. */}
      <circle cx="50" cy="30" r="46" fill={b.glow} opacity="0.35" />
      {b.stars &&
        STAR_POINTS.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 === 0 ? 1.4 : 1} fill="#fff" opacity="0.85" />
        ))}
    </g>
  );
}

// ---------------------------------------------------------------------------
// Characters
// ---------------------------------------------------------------------------

const CHARACTER_PARTS: Record<AvatarCharacter, () => ReactElement> = {
  astronaut: () => (
    <g>
      <circle cx="50" cy="52" r="24" fill="#f3c9a2" />
      <path d="M28 44a22 22 0 0 1 44 0z" fill="#5a3b25" />
      <Eyes />
      <Smile />
      <circle cx="34" cy="55" r="3" fill="#f4a3a3" opacity="0.7" />
      <circle cx="66" cy="55" r="3" fill="#f4a3a3" opacity="0.7" />
    </g>
  ),
  alien: () => (
    <g>
      <path d="M50 28c15 0 23 12 23 24 0 14-11 24-23 24S27 66 27 52c0-12 8-24 23-24z" fill="#8fe36b" />
      <ellipse cx="41" cy="52" rx="5" ry="7" fill="#12331a" transform="rotate(-12 41 52)" />
      <ellipse cx="59" cy="52" rx="5" ry="7" fill="#12331a" transform="rotate(12 59 52)" />
      <circle cx="42" cy="49" r="1.6" fill="#fff" />
      <circle cx="60" cy="49" r="1.6" fill="#fff" />
      <path d="M45 66q5 4 10 0" fill="none" stroke="#3a6b2a" strokeWidth="2" strokeLinecap="round" />
    </g>
  ),
  robot: () => (
    <g>
      <rect x="28" y="32" width="44" height="42" rx="10" fill="#c2cbd6" />
      <rect x="24" y="46" width="4" height="12" rx="2" fill="#8b95a3" />
      <rect x="72" y="46" width="4" height="12" rx="2" fill="#8b95a3" />
      <rect x="34" y="44" width="32" height="16" rx="6" fill="#1c2733" />
      <circle cx="43" cy="52" r="3.4" fill="#4fd6e0" />
      <circle cx="57" cy="52" r="3.4" fill="#4fd6e0" />
      <rect x="40" y="65" width="20" height="3" rx="1.5" fill="#8b95a3" />
      <rect x="47" y="22" width="6" height="8" rx="2" fill="#8b95a3" />
      <circle cx="50" cy="21" r="3" fill="#f4b23d" />
    </g>
  ),
  cow: () => (
    <g>
      {/* ears */}
      <ellipse cx="27" cy="44" rx="7" ry="5" fill="#f2efe9" transform="rotate(-24 27 44)" />
      <ellipse cx="73" cy="44" rx="7" ry="5" fill="#f2efe9" transform="rotate(24 73 44)" />
      {/* small horns */}
      <path d="M38 32c-3-5-8-6-10-4 3 1 5 4 6 7z" fill="#e8d9b5" />
      <path d="M62 32c3-5 8-6 10-4-3 1-5 4-6 7z" fill="#e8d9b5" />
      {/* head */}
      <circle cx="50" cy="52" r="24" fill="#f7f4ee" />
      {/* black spots */}
      <path d="M31 46c4-6 11-4 10 2s-11 6-10-2z" fill="#2b2b2b" />
      <path d="M66 62c5-2 9 3 5 7s-11-4-5-7z" fill="#2b2b2b" />
      <Eyes cy={49} />
      {/* snout */}
      <ellipse cx="50" cy="64" rx="14" ry="9" fill="#f7c6cf" />
      <ellipse cx="44" cy="64" rx="1.8" ry="2.6" fill="#c9808f" />
      <ellipse cx="56" cy="64" rx="1.8" ry="2.6" fill="#c9808f" />
    </g>
  ),
  rocket: () => (
    <g>
      {/* flame */}
      <path d="M50 82c-5-3-6-9-6-9 3 1 4 2 6 2s3-1 6-2c0 0-1 6-6 9z" fill="#ffb454" />
      <path d="M50 78c-2.5-2-3-6-3-6 1.5 1 2 1 3 1s1.5 0 3-1c0 0-.5 4-3 6z" fill="#ff7a3d" />
      {/* fins */}
      <path d="M38 62l-6 10 8-4z" fill="#c74a4a" />
      <path d="M62 62l6 10-8-4z" fill="#c74a4a" />
      {/* body */}
      <path d="M50 22c9 6 12 18 12 30v14H38V52c0-12 3-24 12-30z" fill="#eef1f5" />
      {/* nose */}
      <path d="M50 22c5 3 8 9 9 15H41c1-6 4-12 9-15z" fill="#c74a4a" />
      {/* window */}
      <circle cx="50" cy="50" r="7" fill="#4fb0e0" stroke="#2c6f9e" strokeWidth="2" />
    </g>
  ),
  star: () => (
    <g>
      <path
        d="M50 26l7.6 15.4 17 2.5-12.3 12 2.9 16.9L50 84.9 34.8 72.8l2.9-16.9-12.3-12 17-2.5z"
        fill="#ffd34d"
        stroke="#e0a92e"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <Eyes cx1={44} cx2={56} cy={52} r={2.6} />
      <Smile y={60} w={6} />
    </g>
  ),
  moon: () => (
    <g>
      <circle cx="50" cy="52" r="24" fill="#e9e6d6" />
      <circle cx="40" cy="44" r="4" fill="#cfcbb8" />
      <circle cx="61" cy="60" r="5" fill="#cfcbb8" />
      <circle cx="58" cy="40" r="2.6" fill="#cfcbb8" />
      <Eyes cy={52} r={2.6} />
      <Smile y={61} w={6} />
    </g>
  ),
  planet: () => (
    <g>
      <ellipse cx="50" cy="54" rx="34" ry="9" fill="none" stroke="#d9a25a" strokeWidth="3" opacity="0.9" />
      <circle cx="50" cy="52" r="21" fill="#e08a4a" />
      <path d="M31 48q10 4 20 0t18 2" fill="none" stroke="#c26f34" strokeWidth="2.5" opacity="0.7" />
      <Eyes cy={50} r={2.6} />
      <Smile y={58} w={6} />
    </g>
  ),
};

export function renderCharacter(id: AvatarCharacter): ReactElement {
  return (CHARACTER_PARTS[id] ?? CHARACTER_PARTS.astronaut)();
}

// ---------------------------------------------------------------------------
// Outfits (bottom shoulders / neckwear)
// ---------------------------------------------------------------------------

const OUTFIT_PARTS: Record<AvatarOutfit, () => ReactElement | null> = {
  none: () => null,
  spacesuit: () => (
    <g>
      <path d="M24 100v-6c0-9 12-14 26-14s26 5 26 14v6z" fill="#eef1f5" />
      <rect x="30" y="90" width="40" height="4" rx="2" fill="#ff8a3d" />
      <rect x="44" y="94" width="12" height="6" rx="1.5" fill="#c2cbd6" />
    </g>
  ),
  labcoat: () => (
    <g>
      <path d="M24 100v-6c0-9 12-14 26-14s26 5 26 14v6z" fill="#f6f8fb" />
      <path d="M50 80l-9 20h18z" fill="#5aa9e6" />
      <path d="M50 80l-10 8 2 5 8-6 8 6 2-5z" fill="#dfe6ee" />
    </g>
  ),
  cape: () => (
    <g>
      <path d="M24 100v-6c0-9 12-14 26-14s26 5 26 14v6z" fill="#c0392b" />
      <circle cx="50" cy="83" r="4" fill="#f4c430" />
    </g>
  ),
  overalls: () => (
    <g>
      <path d="M24 100v-6c0-9 12-14 26-14s26 5 26 14v6z" fill="#3f6fb0" />
      <rect x="40" y="80" width="5" height="16" rx="2" fill="#33578c" />
      <rect x="55" y="80" width="5" height="16" rx="2" fill="#33578c" />
      <circle cx="42.5" cy="90" r="1.8" fill="#f4c430" />
      <circle cx="57.5" cy="90" r="1.8" fill="#f4c430" />
    </g>
  ),
  bowtie: () => (
    <g>
      <path d="M24 100v-5c0-8 12-13 26-13s26 5 26 13v5z" fill="#2f3540" />
      <path d="M50 82l-9-5v10z" fill="#c0392b" />
      <path d="M50 82l9-5v10z" fill="#c0392b" />
      <rect x="47" y="79" width="6" height="6" rx="1.5" fill="#8f2519" />
    </g>
  ),
  scarf: () => (
    <g>
      <path d="M32 84c6 5 30 5 36 0l3 6c-7 6-35 6-42 0z" fill="#c14b7a" />
      <path d="M60 88l7 12 6-2-8-13z" fill="#a63a63" />
    </g>
  ),
};

export function renderOutfit(id: AvatarOutfit): ReactElement | null {
  return (OUTFIT_PARTS[id] ?? OUTFIT_PARTS.none)();
}

// ---------------------------------------------------------------------------
// Hats (on top of the head)
// ---------------------------------------------------------------------------

const HAT_PARTS: Record<AvatarHat, () => ReactElement | null> = {
  none: () => null,
  helmet: () => (
    <g>
      <circle cx="50" cy="50" r="30" fill="#bfe3ff" opacity="0.28" />
      <circle cx="50" cy="50" r="30" fill="none" stroke="#e9f4ff" strokeWidth="3" opacity="0.8" />
      <path d="M32 38a26 26 0 0 1 14-9" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" opacity="0.85" />
    </g>
  ),
  party: () => (
    <g>
      <path d="M50 6l13 30H37z" fill="#e14b8a" />
      <path d="M44 22l3 6M54 16l3 8M48 30l3 5" stroke="#ffd34d" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="50" cy="6" r="4" fill="#ffd34d" />
    </g>
  ),
  propeller: () => (
    <g>
      <path d="M32 34a18 12 0 0 1 36 0z" fill="#e14b4b" />
      <path d="M32 34a18 12 0 0 1 36 0" fill="none" stroke="#fff" strokeWidth="0" />
      <path d="M38 34h24" stroke="#f4c430" strokeWidth="3" />
      <rect x="48" y="14" width="4" height="8" rx="2" fill="#7a7f8a" />
      <path d="M50 14l16-4-16 8z" fill="#4fb0e0" />
      <path d="M50 14l-16-4 16 8z" fill="#4fd6e0" />
      <circle cx="50" cy="14" r="2.4" fill="#2c6f9e" />
    </g>
  ),
  halo: () => (
    <g>
      <ellipse cx="50" cy="16" rx="16" ry="5" fill="none" stroke="#ffe066" strokeWidth="4" />
      <ellipse cx="50" cy="16" rx="16" ry="5" fill="none" stroke="#fff6c2" strokeWidth="1.4" />
    </g>
  ),
  crown: () => (
    <g>
      <path d="M32 36l-2-16 10 8 10-12 10 12 10-8-2 16z" fill="#f4c430" stroke="#d9a12a" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="40" cy="30" r="2" fill="#e14b8a" />
      <circle cx="50" cy="28" r="2.2" fill="#4fb0e0" />
      <circle cx="60" cy="30" r="2" fill="#e14b8a" />
    </g>
  ),
  antenna: () => (
    <g>
      <path d="M43 30c-4-8-8-10-12-16" fill="none" stroke="#7a7f8a" strokeWidth="2.4" strokeLinecap="round" />
      <path d="M57 30c4-8 8-10 12-16" fill="none" stroke="#7a7f8a" strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="30" cy="13" r="4" fill="#e14b4b" />
      <circle cx="70" cy="13" r="4" fill="#4fd6e0" />
    </g>
  ),
};

export function renderHat(id: AvatarHat): ReactElement | null {
  return (HAT_PARTS[id] ?? HAT_PARTS.none)();
}
